import axiosInstance from '@/interceptors/axios'
import { PersonnesApi } from '@/clients/replika'
import type { PersonneSchemaLite, PreferencesMailSchema, SuppressionCompteApercuSchema } from '@/clients/replika/api'
import type { ApercuSuppressionCompte, Personne, PreferencesMail } from '@/types'

const api = new PersonnesApi(undefined, undefined, axiosInstance)

export function mapPersonne(p: PersonneSchemaLite): Personne {
  return {
    id: p.id,
    name: p.nom,
    inscrite: p.inscrite,
    joinedAt: p.joined_at ?? undefined,
    avatarVersion: p.avatar_version ?? undefined,
  }
}

function personneLue(raw: PersonneSchemaLite | null | undefined): Personne {
  if (!raw) throw new Error('Personne returned no data')
  return mapPersonne(raw)
}

export async function getMoi(): Promise<Personne> {
  return personneLue((await api.getByExternalIdPersonnesMeGet()).data.data)
}

export async function definirAvatar(file: File): Promise<Personne> {
  return personneLue((await api.definirAvatarPersonnesMeAvatarPost({ file })).data.data)
}

export async function retirerAvatar(): Promise<Personne> {
  return personneLue((await api.retirerAvatarPersonnesMeAvatarDelete()).data.data)
}

/** La version change l'URL à chaque photo : le cache HTTP du navigateur garde chacune sans jamais servir une ancienne. */
export async function fetchAvatar(personneId: string, version: string): Promise<Blob> {
  const response = await api.getAvatarPersonnesPersonneIdAvatarGet({ personneId, v: version }, { responseType: 'blob' })
  return response.data as Blob
}

function mapPreferencesMail(p: PreferencesMailSchema): PreferencesMail {
  return { invitations: p.mail_invitations, rappels: p.mail_rappels }
}

export async function getPreferencesMail(): Promise<PreferencesMail> {
  const raw = (await api.getPreferencesMailPersonnesMePreferencesMailGet()).data.data
  if (!raw) throw new Error('Preferences mail returned no data')
  return mapPreferencesMail(raw)
}

/** Seuls les champs fournis changent. */
export async function updatePreferencesMail(changes: Partial<PreferencesMail>): Promise<PreferencesMail> {
  const response = await api.updatePreferencesMailPersonnesMePreferencesMailPatch({
    preferencesMailFormUpdate: { mail_invitations: changes.invitations, mail_rappels: changes.rappels },
  })
  const raw = response.data.data
  if (!raw) throw new Error('Update preferences mail returned no data')
  return mapPreferencesMail(raw)
}

function mapApercuSuppression(a: SuppressionCompteApercuSchema): ApercuSuppressionCompte {
  return {
    blocages: a.blocages,
    piecesATransmettre: a.pieces_a_transmettre.map((p) => ({
      pieceId: p.piece_id,
      titre: p.titre,
      collaborateurs: p.collaborateurs.map((c) => ({ personneId: c.personne_id, nom: c.nom })),
    })),
    piecesSupprimees: a.pieces_supprimees.map((p) => ({ pieceId: p.piece_id, titre: p.titre, nbLecteurs: p.nb_lecteurs })),
    productionsSupprimees: a.productions_supprimees,
    compagniesSupprimees: a.compagnies_supprimees,
    productionsQuittees: a.productions_quittees,
  }
}

export async function getApercuSuppression(): Promise<ApercuSuppressionCompte> {
  const raw = (await api.getApercuSuppressionPersonnesMeSuppressionGet()).data.data
  if (!raw) throw new Error('Account deletion preview returned no data')
  return mapApercuSuppression(raw)
}

/** `transferts` : le collaborateur choisi pour chaque pièce à transmettre, par identifiant de pièce. */
export async function supprimerCompte(transferts: Record<string, string>): Promise<void> {
  await api.supprimerComptePersonnesMeDelete({
    suppressionCompteForm: {
      transferts: Object.entries(transferts).map(([pieceId, personneId]) => ({ piece_id: pieceId, personne_id: personneId })),
    },
  })
}
